import { Box } from '@mantine/core'
import type { PropsWithChildren, ReactNode } from 'react'
import { clsx } from '@mantine/styles'
import type { To } from 'react-router-dom'

import { SeeAllBtn } from '../../../common/components/SeeAllBtn/SeeAllBtn'

import { useColorTheme } from '@percent/workplace-giving/common/hooks/useColorTheme/useColorTheme'
import { Text, Icon, IconProps } from '@percent/lemonade'
import classes from './DashboardCard.module.scss'

export type DashboardCardProps = Readonly<{
  icon?: IconProps['name']
  iconElement?: ReactNode
  title?: string
  seeAllHref?: To
  gradient?: boolean
  testId?: string
  fullHeight?: boolean
  /** On mobile, lays the icon beside the content instead of above it. */
  compactOnMobile?: boolean
}>

function DashboardIcon({ icon }: Readonly<{ icon: IconProps['name'] }>): JSX.Element {
  return (
    <Box className={classes.TitleIconWrapper}>
      <Icon name={icon} size={5} color="inherit" />
    </Box>
  )
}

function DashboardTitle({ title }: Readonly<{ title: string }>): JSX.Element {
  const { cssVariables } = useColorTheme()

  return (
    <Text size="large" fontWeight="semiBold" color={cssVariables['--black']}>
      {title}
    </Text>
  )
}

export function DashboardTitleRow({
  icon,
  iconElement,
  title,
  seeAllHref
}: Readonly<Pick<DashboardCardProps, 'icon' | 'iconElement' | 'title' | 'seeAllHref'>>): JSX.Element {
  return (
    <Box className={classes.TitleRow}>
      <Box className={classes.TitleWrapper}>
        {icon && <DashboardIcon icon={icon} />}
        {!icon && iconElement && <Box className={classes.TitleIconWrapper}>{iconElement}</Box>}
        {title && <DashboardTitle title={title} />}
      </Box>
      {seeAllHref && (
        <Box className={classes.SeeMoreBtnWrapper}>
          <SeeAllBtn href={seeAllHref} />
        </Box>
      )}
    </Box>
  )
}

export function DashboardCard({
  icon,
  iconElement,
  title,
  seeAllHref,
  gradient = false,
  children,
  testId,
  fullHeight,
  compactOnMobile = false
}: PropsWithChildren<DashboardCardProps>): JSX.Element {
  const showTitleRow = !!icon || !!iconElement || !!title || !!seeAllHref

  return (
    <Box
      className={clsx(classes.DashboardCard, {
        [classes.GradientBackground]: gradient,
        [classes.FullHeight]: fullHeight,
        [classes.CompactOnMobile]: compactOnMobile
      })}
      data-testid={testId}
    >
      <Box className={classes.Body} style={{ height: '100%' }}>
        {showTitleRow && (
          <DashboardTitleRow icon={icon} iconElement={iconElement} title={title} seeAllHref={seeAllHref} />
        )}
        {children}
      </Box>
    </Box>
  )
}
